import { useEffect, useMemo, useRef, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, UserCog, MoreVertical, Pencil, Trash2, Mail, Phone, Search, Eye, Camera, Loader2, MapPin, Calendar, IdCard, Users, Building, Network } from "lucide-react";
import { toast } from "sonner";

const resizePhoto = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const max = 320; let { width, height } = img;
      if (width > height && width > max) { height = height * max / width; width = max; }
      else if (height > max) { width = width * max / height; height = max; }
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL("image/jpeg", 0.75));
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
});

const Avatar = ({ e, size = 48 }) => e.photo
  ? <img src={e.photo} alt="" className="rounded-full object-cover" style={{ height: size, width: size }} />
  : <div className="flex items-center justify-center rounded-full bg-[#1C3144] text-white font-bold" style={{ height: size, width: size }}>{(e.prenom?.[0] || "") + (e.nom?.[0] || "")}</div>;

function OrgChart({ list, departments, poles, onView }) {
  const activeDepts = departments.filter((d) => d.actif !== false);
  const allPoles = [...new Set([...(poles || []), ...list.map((e) => e.pole).filter(Boolean)])];
  const deptsOfPole = (pole) => [...new Set([
    ...activeDepts.filter((d) => (d.pole || "") === pole).map((d) => d.nom),
    ...list.filter((e) => (e.pole || "") === pole).map((e) => e.departement).filter(Boolean),
  ])];
  const inDept = (pole, dept) => list.filter((e) => (e.pole || "") === pole && (e.departement || "") === dept);
  const noPole = list.filter((e) => !e.pole);
  const Card = ({ e }) => (
    <button onClick={() => onView(e)} data-testid={`org-emp-${e.id}`} className="flex items-center gap-2 w-full text-left rounded-lg border bg-white px-2.5 py-1.5 hover:bg-slate-50">
      <Avatar e={e} size={30} />
      <div className="min-w-0"><p className="text-sm font-medium truncate">{e.prenom} {e.nom}</p><p className="text-[11px] text-slate-400 truncate">{e.fonction || "—"}</p></div>
    </button>
  );
  return (
    <div className="space-y-5" data-testid="org-chart">
      {allPoles.map((pole) => {
        const depts = deptsOfPole(pole);
        if (!depts.length) return null;
        return (
          <div key={pole} className="rounded-xl border bg-slate-50 p-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-600 text-white px-4 py-1.5 mb-3"><Building className="h-4 w-4" /><span className="font-head font-bold text-sm">{pole}</span></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {depts.map((dept) => (
                <div key={dept} className="rounded-lg border bg-white p-3">
                  <p className="text-xs font-mono uppercase text-slate-400 mb-2">{dept} <span className="text-slate-300">({inDept(pole, dept).length})</span></p>
                  <div className="space-y-1.5">{inDept(pole, dept).length === 0 ? <p className="text-xs text-slate-300">—</p> : inDept(pole, dept).map((e) => <Card key={e.id} e={e} />)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {noPole.length > 0 && (
        <div className="rounded-xl border bg-slate-50 p-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-400 text-white px-4 py-1.5 mb-3"><span className="font-head font-bold text-sm">Non assigné à un pôle</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{noPole.map((e) => <Card key={e.id} e={e} />)}</div>
        </div>
      )}
    </div>
  );
}

function HierarchyTree({ list, onView }) {
  if (!list.length) return <EmptyState icon={Network} title="Aucun collaborateur" subtitle="Ajoutez des collaborateurs avec un responsable pour bâtir la hiérarchie." />;
  const byId = Object.fromEntries(list.map((e) => [e.id, e]));
  const childrenOf = {};
  list.forEach((e) => {
    const pid = (e.responsable_id && byId[e.responsable_id]) ? e.responsable_id : "__root__";
    (childrenOf[pid] = childrenOf[pid] || []).push(e);
  });
  const rows = [];
  const walk = (pid, depth) => {
    (childrenOf[pid] || []).forEach((e) => { rows.push({ e, depth }); if (depth < 12) walk(e.id, depth + 1); });
  };
  walk("__root__", 0);
  return (
    <div className="rounded-xl border bg-slate-50 p-4" data-testid="hierarchy-tree">
      <p className="text-xs text-slate-500 mb-2">Arbre construit à partir du responsable de chaque collaborateur.</p>
      {rows.map(({ e, depth }) => (
        <div key={e.id} className="mt-1.5">
          <button onClick={() => onView(e)} data-testid={`hier-emp-${e.id}`} style={{ marginLeft: depth * 20 }} className="flex items-center gap-2 text-left rounded-lg border bg-white px-3 py-1.5 hover:bg-slate-50">
            {depth > 0 && <span className="text-slate-300">&#8627;</span>}
            <Avatar e={e} size={28} />
            <div><p className="text-sm font-medium">{e.prenom} {e.nom}</p><p className="text-[11px] text-slate-400">{e.fonction || "—"}{e.departement ? ` · ${e.departement}` : ""}</p></div>
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Employees() {
  const [items, setItems] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("Tous");
  const [view, setView] = useState("liste");
  const [q, setQ] = useState("");
  const [fPole, setFPole] = useState("all");
  const [fFonction, setFFonction] = useState("all");
  const [fStatut, setFStatut] = useState("all");
  const fileRef = useRef();

  const load = () => api.get("/employees").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const deptNames = useMemo(() => departments.filter((d) => d.actif !== false).map((d) => d.nom), [departments]);
  const empty = () => ({ prenom: "", nom: "", matricule: "", photo: "", date_naissance: "", lieu_naissance: "",
    situation_matrimoniale: "", nombre_enfants: 0, adresse: "", telephone: "", telephone_pro: "", sexe: "",
    numero_securite_sociale: "", email: "", type_piece: "",
    numero_piece: "", fonction: "", departement: "", pole: "", responsable_name: "", date_entree: "", statut: "Actif" });

  const openNew = () => { setForm(empty()); setOpen(true); };
  const openEdit = (e) => { setForm({ ...empty(), ...e }); setOpen(true); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onPhoto = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    const b64 = await resizePhoto(f); set("photo", b64); toast.success("Photo ajoutée");
  };

  const save = async (e) => {
    e.preventDefault(); if (!form.prenom || !form.nom) { toast.error("Nom et prénom requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, nombre_enfants: Number(form.nombre_enfants) || 0 };
      if (form.id) await api.put(`/employees/${form.id}`, payload); else await api.post("/employees", payload);
      toast.success("Collaborateur enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/employees/${id}`); toast.success("Supprimé"); load(); };

  const list = items || [];
  const fonctions = settings.functions || [];
  const poles = settings.poles || [];
  const statuses = settings.collaborator_statuses || ["Actif", "Inactif"];

  const filtered = list.filter((e) => {
    if (tab !== "Tous" && e.departement !== tab) return false;
    if (fPole !== "all" && e.pole !== fPole) return false;
    if (fFonction !== "all" && e.fonction !== fFonction) return false;
    if (fStatut !== "all" && e.statut !== fStatut) return false;
    if (q && !`${e.prenom} ${e.nom} ${e.matricule} ${e.email} ${e.telephone} ${e.fonction}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const countFor = (d) => list.filter((e) => d === "Tous" || e.departement === d).length;
  const tabs = ["Tous", ...deptNames];

  const DField = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2 text-sm"><Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[11px] uppercase text-slate-400">{label}</p><p className="text-slate-700">{value}</p></div></div>
  ) : null;

  return (
    <div>
      <PageHeader title="Collaborateurs" subtitle="Fiches détaillées, organisées par département">
        <div className="flex rounded-lg border overflow-hidden">
          <button onClick={() => setView("liste")} data-testid="view-liste" className={`px-3 py-1.5 text-sm ${view === "liste" ? "bg-brand-600 text-white" : "bg-white text-slate-600"}`}>Liste</button>
          <button onClick={() => setView("organigramme")} data-testid="view-org" className={`px-3 py-1.5 text-sm flex items-center gap-1 ${view === "organigramme" ? "bg-brand-600 text-white" : "bg-white text-slate-600"}`}><Network className="h-4 w-4" />Organigramme</button>
          <button onClick={() => setView("hierarchie")} data-testid="view-hierarchie" className={`px-3 py-1.5 text-sm flex items-center gap-1 border-l ${view === "hierarchie" ? "bg-brand-600 text-white" : "bg-white text-slate-600"}`}><Network className="h-4 w-4" />Hiérarchie</button>
        </div>
        <Button onClick={openNew} data-testid="add-employee" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau collaborateur</Button>
      </PageHeader>

      {view === "organigramme" && <OrgChart list={list} departments={departments} poles={poles} onView={setDetail} />}
      {view === "hierarchie" && <HierarchyTree list={list} onView={setDetail} />}
      {view === "liste" && (<>

      <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-thin pb-1" data-testid="department-tabs">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`dept-tab-${t}`}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${tab === t ? "bg-brand-600 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"}`}>
            {t} <span className="text-xs opacity-70">({countFor(t)})</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <div className="relative col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" data-testid="employee-search" />
        </div>
        <Select value={fPole} onValueChange={setFPole}><SelectTrigger data-testid="filter-pole"><SelectValue placeholder="Pôle" /></SelectTrigger><SelectContent><SelectItem value="all">Tous les pôles</SelectItem>{poles.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={fFonction} onValueChange={setFFonction}><SelectTrigger data-testid="filter-fonction"><SelectValue placeholder="Fonction" /></SelectTrigger><SelectContent><SelectItem value="all">Toutes fonctions</SelectItem>{fonctions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={fStatut} onValueChange={setFStatut}><SelectTrigger data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger><SelectContent><SelectItem value="all">Tous statuts</SelectItem>{statuses.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={UserCog} title="Aucun collaborateur" subtitle="Ajoutez les fiches de vos collaborateurs." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((e) => (
            <div key={e.id} className="rounded-xl border bg-white p-5 card-hover" data-testid={`employee-card-${e.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar e={e} />
                  <div className="min-w-0"><p className="font-head font-bold text-slate-900 truncate">{e.prenom} {e.nom}</p><p className="text-xs text-slate-500 truncate">{e.fonction || "—"}</p></div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDetail(e)} data-testid={`employee-view-${e.id}`}><Eye className="h-4 w-4 mr-2" />Voir la fiche</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(e.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {e.departement && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{e.departement}</span>}
                {e.pole && <span className="rounded-md bg-brand-50 px-2 py-0.5 text-xs text-brand-700">{e.pole}</span>}
                <StatusBadge value={e.statut} />
                <span className="font-mono text-[10px] text-slate-400">{e.matricule}</span>
              </div>
              <div className="mt-3 pt-3 border-t space-y-1 text-xs text-slate-500">
                {e.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{e.email}</p>}
                {e.telephone && <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{e.telephone}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}

      {/* Form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier le collaborateur" : "Nouveau collaborateur"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar e={form} size={72} />
                <div>
                  <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onPhoto} data-testid="employee-photo-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Camera className="h-4 w-4 mr-1.5" />Photo</Button>
                  {form.photo && <button type="button" onClick={() => set("photo", "")} className="ml-2 text-xs text-rose-500">Retirer</button>}
                </div>
              </div>
              <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700">Informations personnelles</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Prénom *</Label><Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} required className="mt-1" data-testid="emp-prenom" /></div>
                <div><Label className="text-xs">Nom *</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} required className="mt-1" data-testid="emp-nom" /></div>
                <div><Label className="text-xs">Matricule</Label><Input value={form.matricule} onChange={(e) => set("matricule", e.target.value)} placeholder="Auto si vide" className="mt-1" /></div>
                <div><Label className="text-xs">Date de naissance</Label><Input type="date" value={form.date_naissance || ""} onChange={(e) => set("date_naissance", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Lieu de naissance</Label><Input value={form.lieu_naissance || ""} onChange={(e) => set("lieu_naissance", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Situation matrimoniale</Label>
                  <Select value={form.situation_matrimoniale || ""} onValueChange={(v) => set("situation_matrimoniale", v)}><SelectTrigger className="mt-1" data-testid="emp-matrimonial"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{(settings.matrimonial_statuses || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Enfants à charge</Label><Input type="number" min="0" value={form.nombre_enfants} onChange={(e) => set("nombre_enfants", e.target.value)} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Adresse</Label><Input value={form.adresse || ""} onChange={(e) => set("adresse", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Téléphone</Label><Input value={form.telephone || ""} onChange={(e) => set("telephone", e.target.value)} className="mt-1" data-testid="emp-tel" /></div>
                <div><Label className="text-xs">Téléphone professionnel</Label><Input value={form.telephone_pro || ""} onChange={(e) => set("telephone_pro", e.target.value)} className="mt-1" data-testid="emp-tel-pro" /></div>
                <div><Label className="text-xs">Sexe</Label>
                  <Select value={form.sexe || ""} onValueChange={(v) => set("sexe", v)}><SelectTrigger className="mt-1" data-testid="emp-sexe"><SelectValue placeholder="—" /></SelectTrigger><SelectContent><SelectItem value="Masculin">Masculin</SelectItem><SelectItem value="Féminin">Féminin</SelectItem></SelectContent></Select></div>
                <div className="md:col-span-2"><Label className="text-xs">N° de sécurité sociale</Label><Input value={form.numero_securite_sociale || ""} onChange={(e) => set("numero_securite_sociale", e.target.value)} className="mt-1" data-testid="emp-secu" /></div>
                <div className="md:col-span-2"><Label className="text-xs">E-mail</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="emp-email" /></div>
                <div><Label className="text-xs">Type de pièce</Label>
                  <Select value={form.type_piece || ""} onValueChange={(v) => set("type_piece", v)}><SelectTrigger className="mt-1" data-testid="emp-idtype"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{(settings.id_types || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div className="md:col-span-2"><Label className="text-xs">N° de pièce d'identité</Label><Input value={form.numero_piece || ""} onChange={(e) => set("numero_piece", e.target.value)} className="mt-1" /></div>
              </div>
              <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700">Situation professionnelle</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Fonction</Label>
                  <Select value={form.fonction || ""} onValueChange={(v) => set("fonction", v)}><SelectTrigger className="mt-1" data-testid="emp-fonction"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{fonctions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Département</Label>
                  <Select value={form.departement || ""} onValueChange={(v) => set("departement", v)}><SelectTrigger className="mt-1" data-testid="emp-departement"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{deptNames.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Pôle</Label>
                  <Select value={form.pole || ""} onValueChange={(v) => set("pole", v)}><SelectTrigger className="mt-1" data-testid="emp-pole"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{poles.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Responsable</Label><Input value={form.responsable_name || ""} onChange={(e) => set("responsable_name", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Date d'embauche</Label><Input type="date" value={form.date_entree || ""} onChange={(e) => set("date_entree", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Statut</Label>
                  <Select value={form.statut || "Actif"} onValueChange={(v) => set("statut", v)}><SelectTrigger className="mt-1" data-testid="emp-statut"><SelectValue /></SelectTrigger><SelectContent>{statuses.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} data-testid="employee-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-head flex items-center gap-3"><Avatar e={detail} size={44} />{detail.prenom} {detail.nom}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              {detail.fonction && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs">{detail.fonction}</span>}
              {detail.departement && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs">{detail.departement}</span>}
              {detail.pole && <span className="rounded-md bg-brand-50 px-2 py-0.5 text-xs text-brand-700">{detail.pole}</span>}
              <StatusBadge value={detail.statut} /><span className="font-mono text-[10px] text-slate-400">{detail.matricule}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <DField icon={Calendar} label="Date de naissance" value={detail.date_naissance ? fmtDate(detail.date_naissance) : ""} />
              <DField icon={MapPin} label="Lieu de naissance" value={detail.lieu_naissance} />
              <DField icon={Users} label="Situation matrimoniale" value={detail.situation_matrimoniale} />
              <DField icon={Users} label="Enfants à charge" value={detail.nombre_enfants ? String(detail.nombre_enfants) : ""} />
              <DField icon={MapPin} label="Adresse" value={detail.adresse} />
              <DField icon={Phone} label="Téléphone" value={detail.telephone} />
              <DField icon={Mail} label="E-mail" value={detail.email} />
              <DField icon={IdCard} label="Pièce d'identité" value={[detail.type_piece, detail.numero_piece].filter(Boolean).join(" · ")} />
              <DField icon={Calendar} label="Date d'embauche" value={detail.date_entree ? fmtDate(detail.date_entree) : ""} />
              <DField icon={UserCog} label="Responsable" value={detail.responsable_name} />
            </div>
            <DialogFooter><Button variant="outline" onClick={() => { setDetail(null); openEdit(detail); }}><Pencil className="h-4 w-4 mr-1.5" />Modifier</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
